import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePaginatedApi, useFormSubmit, useAsyncOperation, useApi } from '../hooks/useApi';
import { driversAPI, trucksAPI, transporteursAPI, systemConfigAPI } from '../services/api';
import { CLIENT_FLEET_MAINTENANCE_CONFIG_KEY } from '../constants/systemConfigKeys';
import Table from '../components/Table';
import Button from '../components/Button';
import Input from '../components/Input';
import MultiSelect from '../components/MultiSelect';
import Modal, { ConfirmModal } from '../components/Modal';
import { UserGroupIcon, PlusIcon, EyeIcon, PencilIcon, TrashIcon, KeyIcon } from '@heroicons/react/24/outline';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';

const isValidDriverEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || '').trim());

const DriversPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { isManagerOrAdmin, isOperator } = useAuth();
  const { data: clientFleetMaintenanceConfig } = useApi(
    isOperator() ? () => systemConfigAPI.getByKey(CLIENT_FLEET_MAINTENANCE_CONFIG_KEY) : null
  );
  const clientFleetMaintenanceEnabled = Boolean(clientFleetMaintenanceConfig?.value ?? clientFleetMaintenanceConfig?.data?.value);
  const canMaintainDrivers = isManagerOrAdmin() || (isOperator() && clientFleetMaintenanceEnabled);
  const [search, setSearch] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isCreateLoginModalOpen, setIsCreateLoginModalOpen] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const searchTimeoutRef = useRef(null);

  // Fetch trucks and transporteurs for the form
  const { data: trucksData } = useApi(() => trucksAPI.getAll({ limit: 500 }));
  const trucks = trucksData?.trucks || [];
  const { data: transporteursData } = useApi(() => transporteursAPI.getAll({ limit: 500 }));
  const transporteurs = transporteursData?.transporteurs || [];

  const {
    data: drivers,
    pagination,
    loading,
    updateParams,
    goToPage,
    refresh
  } = usePaginatedApi(driversAPI.getAll, { search });

  const { submit: submitCreate, loading: createLoading } = useFormSubmit(driversAPI.create, {
    showSuccessToast: false,
    onSuccess: (data) => {
      setIsAddModalOpen(false);
      refresh();
      toast.success(data?.reusedExisting ? t('drivers.reusedExisting') : t('drivers.created'));
    },
  });

  const { submit: submitUpdate, loading: updateLoading } = useFormSubmit(
    (data) => driversAPI.update(selectedDriver?.id, data),
    {
      onSuccess: () => {
        setIsEditModalOpen(false);
        setSelectedDriver(null);
        refresh();
        toast.success(t('drivers.updated'));
      },
    }
  );

  const { execute, loading: deleteLoading } = useAsyncOperation();

  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => updateParams({ search }), 300);
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [search, updateParams]);

  const handleView = (row) => {
    navigate(`/transporteurs/drivers/${row.id}`);
  };

  const handleEdit = (row) => {
    setSelectedDriver(row);
    setIsEditModalOpen(true);
  };

  const handleDelete = (row) => {
    setSelectedDriver(row);
    setIsDeleteModalOpen(true);
  };

  const handleCreateLogin = (row) => {
    if (!isValidDriverEmail(row?.email)) {
      toast.error(t('drivers.loginRequiresValidEmail'));
      return;
    }
    setSelectedDriver(row);
    setIsCreateLoginModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedDriver) return;
    try {
      await execute(
        () => driversAPI.delete(selectedDriver.id),
        { showSuccessToast: true, successMessage: t('drivers.deleted') }
      );
      setIsDeleteModalOpen(false);
      setSelectedDriver(null);
      refresh();
    } catch (_) {}
  };

  const columns = [
    {
      key: 'name',
      title: t('drivers.name'),
      render: (_, row) => (
        <span className="font-medium text-gray-900">
          {row.firstName} {row.lastName}
        </span>
      ),
    },
    {
      key: 'phone',
      title: t('drivers.phone'),
      render: (value) => <span className="text-sm text-gray-600">{value || '—'}</span>,
    },
    {
      key: 'transporteurs',
      title: t('drivers.transporteursFromTrucks'),
      render: (_, row) => (
        <span className="text-sm text-gray-600">
          {row.transporteurs?.length ? row.transporteurs.map(t => t.companyName).join(', ') : '—'}
        </span>
      ),
    },
    {
      key: '_count',
      title: t('drivers.trucks'),
      render: (_, row) => (
        <span className="text-sm text-gray-600">{row._count?.truckDrivers ?? 0}</span>
      ),
    },
    {
      key: 'actions',
      title: t('tables.actions'),
      sticky: true,
      width: '240px',
      minWidth: '240px',
      render: (_, row) => (
        <div className="flex items-center gap-2 flex-nowrap">
          <Button size="sm" variant="ghost" onClick={() => handleView(row)} title={t('common.view')} className="shrink-0">
            <EyeIcon className="h-4 w-4" />
          </Button>
          {canMaintainDrivers && (
            <Button size="sm" variant="ghost" onClick={() => handleEdit(row)} title={t('common.edit')} className="shrink-0">
              <PencilIcon className="h-4 w-4" />
            </Button>
          )}
          {!row.user && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => handleCreateLogin(row)}
              title={isValidDriverEmail(row?.email) ? t('drivers.createLogin') : t('drivers.loginRequiresValidEmail')}
              className="shrink-0"
              disabled={!isValidDriverEmail(row?.email)}
            >
              <KeyIcon className="h-4 w-4" />
            </Button>
          )}
          {canMaintainDrivers && (() => {
            const loadCount = Number(row?._count?.loads || 0);
            const canDelete = loadCount === 0;
            return (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => handleDelete(row)}
            title={canDelete ? t('common.delete') : t('drivers.cannotDeleteWithLoads')}
            className={`shrink-0 ${canDelete ? 'text-red-600 hover:text-red-700' : 'text-gray-400 cursor-not-allowed'}`}
            disabled={!canDelete}
          >
            <TrashIcon className="h-4 w-4" />
          </Button>
            );
          })()}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center space-x-2 sm:space-x-3">
          <UserGroupIcon className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600" />
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{t('drivers.title')}</h1>
            <p className="text-sm sm:text-base text-gray-600 hidden sm:block">{t('drivers.subtitle')}</p>
          </div>
        </div>
        {canMaintainDrivers && (
          <Button onClick={() => setIsAddModalOpen(true)}>
            <PlusIcon className="h-4 w-4 mr-2" />
            {t('drivers.addDriver')}
          </Button>
        )}
      </div>

      <div className="card">
        <div className="flex flex-col sm:flex-row gap-4 mb-4">
          <Input
            placeholder={t('common.search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1"
          />
        </div>
        <Table
          columns={columns}
          data={drivers}
          loading={loading}
          pagination={pagination}
          onPageChange={goToPage}
          emptyMessage={t('drivers.noDriversFound')}
        />
      </div>

      <DriverForm
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={submitCreate}
        loading={createLoading}
        title={t('drivers.newDriver')}
        submitText={t('common.create')}
        trucks={trucks}
        transporteurs={transporteurs}
      />
      <DriverForm
        isOpen={isEditModalOpen}
        onClose={() => { setIsEditModalOpen(false); setSelectedDriver(null); }}
        onSubmit={submitUpdate}
        loading={updateLoading}
        title={t('drivers.editDriver')}
        submitText={t('common.update')}
        initialData={selectedDriver}
        trucks={trucks}
        transporteurs={transporteurs}
      />
      <CreateLoginModal
        isOpen={isCreateLoginModalOpen}
        onClose={() => { setIsCreateLoginModalOpen(false); setSelectedDriver(null); }}
        driver={selectedDriver}
        onSuccess={() => { refresh(); setIsCreateLoginModalOpen(false); setSelectedDriver(null); toast.success(t('drivers.loginCreated')); }}
      />
      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => { setIsDeleteModalOpen(false); setSelectedDriver(null); }}
        onConfirm={confirmDelete}
        loading={deleteLoading}
        title={t('drivers.deleteDriver')}
        message={t('drivers.confirmDelete', { name: selectedDriver ? `${selectedDriver.firstName} ${selectedDriver.lastName}` : '' })}
        confirmText={t('common.delete')}
        variant="danger"
      />
    </div>
  );
};

const DriverForm = ({ isOpen, onClose, onSubmit, loading, title, submitText, initialData = {}, trucks = [], transporteurs = [] }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    status: 'active',
    truckIds: [],
  });

  React.useEffect(() => {
    if (isOpen) {
      const existingTruckIds = initialData?.truckDrivers?.map(td => td.truck?.id).filter(Boolean) || [];
      setFormData({
        firstName: initialData?.firstName || '',
        lastName: initialData?.lastName || '',
        phone: initialData?.phone || '',
        email: initialData?.email || '',
        status: initialData?.status || 'active',
        truckIds: existingTruckIds,
      });
    }
  }, [isOpen, initialData?.id]); // eslint-disable-line react-hooks/exhaustive-deps -- initialize driver form when opening or switching the edited driver

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="lg" closeOnOverlayClick={false}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={loading}>{t('common.cancel')}</Button>
          <Button type="submit" loading={loading} form="driver-form">{submitText}</Button>
        </>
      }
    >
      <form id="driver-form" onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input label={t('drivers.firstName')} name="firstName" value={formData.firstName} onChange={(e) => setFormData({ ...formData, [e.target.name]: e.target.value })} required />
          <Input label={t('drivers.lastName')} name="lastName" value={formData.lastName} onChange={(e) => setFormData({ ...formData, [e.target.name]: e.target.value })} required />
          <Input label={t('drivers.phone')} name="phone" value={formData.phone} onChange={(e) => setFormData({ ...formData, [e.target.name]: e.target.value })} />
          <Input label={t('drivers.email')} name="email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, [e.target.name]: e.target.value })} />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('drivers.status')}</label>
            <select
              name="status"
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            >
              <option value="active">{t('drivers.active')}</option>
              <option value="inactive">{t('drivers.inactive')}</option>
            </select>
          </div>
        </div>

        <MultiSelect
          label={t('drivers.selectTrucks')}
          name="truckIds"
          values={formData.truckIds}
          onChange={(e) => setFormData({ ...formData, truckIds: e.target.value })}
          options={trucks.map((truck) => ({
            value: truck.id,
            label: `${truck.plateNumber}${truck.transporteurs?.length > 0 ? ` (${truck.transporteurs.map(t => t.companyName).join(', ')})` : ''}`
          }))}
          placeholder={t('drivers.selectTrucks')}
        />
      </form>
    </Modal>
  );
};

const CreateLoginModal = ({ isOpen, onClose, driver, onSuccess }) => {
  const { t } = useTranslation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  /** Browsers often ignore autocomplete=off on credential-shaped fields; readOnly until focus avoids saved-login injection in modals. */
  const [fieldsUnlocked, setFieldsUnlocked] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setUsername('');
      setPassword('');
      setFieldsUnlocked(false);
      return;
    }
    setUsername('');
    setPassword('');
    setFieldsUnlocked(false);
  }, [isOpen, driver?.id]);
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!driver?.id || !username.trim() || !password) return;
    setLoading(true);
    try {
      await driversAPI.createLogin(driver.id, { username: username.trim(), password });
      onSuccess();
      onClose();
    } catch (err) {
      const msg = err.response?.data?.message || t('common.error');
      alert(msg);
    } finally {
      setLoading(false);
    }
  };
  if (!driver) return null;
  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('drivers.createLogin')} size="md"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={loading}>{t('common.cancel')}</Button>
          <Button onClick={handleSubmit} loading={loading} disabled={!username.trim() || !password}>{t('common.create')}</Button>
        </>
      }
    >
      <p className="text-sm text-gray-600 mb-4">{t('drivers.createLoginFor', { name: `${driver.firstName} ${driver.lastName}` })}</p>
      <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
        <Input
          label={t('drivers.username')}
          name="driver_new_login_identifier"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          onFocus={() => setFieldsUnlocked(true)}
          readOnly={!fieldsUnlocked}
          autoComplete="off"
          required
        />
        <Input
          label={t('common.password')}
          type="password"
          name="driver_new_login_secret"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onFocus={() => setFieldsUnlocked(true)}
          readOnly={!fieldsUnlocked}
          autoComplete="new-password"
          required
          minLength={6}
        />
      </form>
    </Modal>
  );
};

export default DriversPage;
